import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import AssistantScrollToBottomButton from './AssistantScrollToBottomButton'

const DYNAMIC_SPACER_HEIGHT_VAR = '--assistant-chat-dynamic-spacer-height'
const FIT_AVAILABLE_HEIGHT_CLASS_NAME = 'fit-available-height'
const WITH_TIMELINE_CLASS_NAME = 'with-timeline'
const COMPACT_MESSAGES_PADDING_MAX_WIDTH = '768px'

const ChatColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
`

const MessagesWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: ${rem(20)} ${rem(20)} ${rem(24)};

  @media (max-width: ${COMPACT_MESSAGES_PADDING_MAX_WIDTH}) {
    padding: ${rem(20)};
  }

  &.${WITH_TIMELINE_CLASS_NAME} {
    padding-inline: ${rem(36)};
  }
`

const TopSlotWrapper = styled.div`
  position: relative;
  z-index: 3;
  flex-shrink: 0;
`

const InputAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`

const ChatOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: ${props => transparentize(0.97, props.theme.palette.neutral.surface.darkBackground)};
  display: block;
  pointer-events: all;
`

const ChatContainer = styled(Group)`
  height: 100%;
  min-height: ${rem(520)};
  border: none;
  border-radius: 0;
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.fit-available-height {
    min-height: 0;
  }
`

const BottomSpacer = styled.div`
  min-height: var(${DYNAMIC_SPACER_HEIGHT_VAR}, 0);
  flex-shrink: 0;
`

type BottomSpacerStyle = React.CSSProperties & {
  [DYNAMIC_SPACER_HEIGHT_VAR]: string
}

interface Props {
  children: React.ReactNode
  messagesWrapperRef: React.MutableRefObject<HTMLDivElement | null>
  onScroll: () => void
  isOverlay: boolean
  showHistoryOverlay: boolean
  onHistoryWheel?: (e: React.WheelEvent<HTMLDivElement>) => void
  onHistoryTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void
  onHistoryTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void
  onHistoryTouchEnd?: () => void
  topSlot?: React.ReactNode
  inputSlot?: React.ReactNode
  onPullToCollapseTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void
  onPullToCollapseTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void
  onPullToCollapseTouchEnd?: () => void
  isAtBottom?: boolean
  hasContentOutOfView?: boolean
  onScrollToBottomButtonClick?: () => void
  isViewingHistory?: boolean
  dynamicSpacerHeight?: number
  fitAvailableHeight?: boolean
  timelineSlot?: React.ReactNode
}

export default function AssistantChatPanel({
  children,
  messagesWrapperRef,
  onScroll,
  isOverlay,
  showHistoryOverlay,
  onHistoryWheel = noop,
  onHistoryTouchStart = noop,
  onHistoryTouchMove = noop,
  onHistoryTouchEnd = noop,
  topSlot,
  inputSlot,
  onPullToCollapseTouchStart = noop,
  onPullToCollapseTouchMove = noop,
  onPullToCollapseTouchEnd = noop,
  isAtBottom = true,
  hasContentOutOfView = false,
  onScrollToBottomButtonClick = noop,
  isViewingHistory = false,
  dynamicSpacerHeight = 0,
  fitAvailableHeight = false,
  timelineSlot,
}: Props) {
  const showScrollButton = hasContentOutOfView && !isAtBottom && !isViewingHistory && !isOverlay
  const chatContainerClassName = clsx({
    [FIT_AVAILABLE_HEIGHT_CLASS_NAME]: fitAvailableHeight,
  })
  const messagesWrapperClassName = clsx({
    [WITH_TIMELINE_CLASS_NAME]: timelineSlot,
  })
  const bottomSpacerStyle = React.useMemo<BottomSpacerStyle>(() => ({
    [DYNAMIC_SPACER_HEIGHT_VAR]: rem(dynamicSpacerHeight),
  }), [dynamicSpacerHeight])

  return (
    <ChatColumn>
      <ChatContainer
        className={chatContainerClassName}
        direction="vertical"
        gap={0}
        fullWidth
      >
        {topSlot && <TopSlotWrapper>{topSlot}</TopSlotWrapper>}
        <MessagesWrapper
          className={messagesWrapperClassName}
          ref={messagesWrapperRef}
          onScroll={onScroll}
          onTouchStart={onPullToCollapseTouchStart}
          onTouchMove={onPullToCollapseTouchMove}
          onTouchEnd={onPullToCollapseTouchEnd}
        >
          {timelineSlot}
          {children}
          <BottomSpacer data-spacer style={bottomSpacerStyle} />
        </MessagesWrapper>
        {!isOverlay && (
          <InputAreaWrapper>
            <AssistantScrollToBottomButton visible={showScrollButton} onClick={onScrollToBottomButtonClick} />
            {inputSlot}
          </InputAreaWrapper>
        )}
        {showHistoryOverlay && (
          <ChatOverlay
            role="presentation"
            aria-hidden="true"
            onWheel={onHistoryWheel}
            onTouchStart={onHistoryTouchStart}
            onTouchMove={onHistoryTouchMove}
            onTouchEnd={onHistoryTouchEnd}
            onTouchCancel={onHistoryTouchEnd}
          />
        )}
      </ChatContainer>
    </ChatColumn>
  )
}
